import { FeatureExtractionContextType } from '@mobile-mapping/providers';
import { type GeospatialFeature } from '@trimble/geospatial';
import {
  FeaturesServiceFeatureEvent,
  GeoJsonFeature,
} from '@trimble/web3d-plugin-features-service';
import { UnreachableCaseError } from 'ts-essentials';
import { LAYER_ID_METADATA_KEY } from './feature-extraction-cad-config';

export interface CreateFeatureIngestBufferParams {
  readonly context: FeatureExtractionContextType;
  isDisposed(): boolean;
}

export interface FeatureIngestBuffer {
  handleFeatureEvent(event: FeaturesServiceFeatureEvent): void;
}

export function createFeatureIngestBuffer(
  params: CreateFeatureIngestBufferParams
): FeatureIngestBuffer {
  let ingestBuffer: GeospatialFeature[] = [];
  let flushScheduled = false;

  const { context, isDisposed } = params;

  const scheduleIngestFlush = (): void => {
    if (flushScheduled) {
      return;
    }
    flushScheduled = true;
    queueMicrotask(() => {
      flushScheduled = false;
      if (isDisposed() || ingestBuffer.length === 0) {
        return;
      }
      const bufferedFeatures = ingestBuffer;
      ingestBuffer = [];
      context.upsertFeatures(bufferedFeatures);
    });
  };
  const handleFeatureEvent = (event: FeaturesServiceFeatureEvent): void => {
    if (isDisposed()) {
      return;
    }
    switch (event.type) {
      case 'feature:ingested': {
        const converted = ingestedToGeospatialFeature(event.feature);
        if (converted) {
          ingestBuffer.push(converted);
          scheduleIngestFlush();
        }
        break;
      }
      case 'feature:removed':
        // Tile cull — shared map removes only on true delete.
        break;
      default:
        throw new UnreachableCaseError(event);
    }
  };

  return {
    handleFeatureEvent,
  };
}

function ingestedToGeospatialFeature(
  feature: GeoJsonFeature
): GeospatialFeature | undefined {
  if (feature.metadata?.['cluster'] === true) {
    return undefined;
  }
  if (feature.geometry.type === 'Polygon') {
    return undefined;
  }
  return {
    id: feature.id,
    layerId: readString(feature.metadata?.[LAYER_ID_METADATA_KEY]) ?? '',
    geometry: feature.geometry,
    properties: { ...(feature.properties ?? {}) },
    metadata: feature.metadata ?? {},
  };
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
